import { Loadable, LoadableState, OrderCurrencySourceCurrencyWithBalance } from '@eih-netbroker/common';
import { useCallback } from 'react';
import { useLocale } from '../../../../../context/NbLocalizationContext';
import { CurrencyFlag } from '../../../../../shared/components/currency-flag/CurrencyFlag';
import { NbIndefiniteLoaderValueWrapper } from '../../../../../shared/components/loader-value/NbIndefiniteLoaderValueWrapper';
import { MoneyMaskHOC } from '../../../../../shared/components/money-mask/MoneyMask';
import { useFormatter } from '../../../../../shared/services/utils/useFormatter';
import { cssWhen } from '../../../../../shared/utils/themeUtils';
import { OrderFormSourceComponent } from '../../../types/orderTypes';
import classes from './OrderCurrencyExchangeCurrencySelector.module.scss';
import { OrderCurrencyExchangeSelectionCard } from './OrderCurrencyExchangeSelectionCard';

export type CurrencyOption = {
  code: string;
  balance: string;
  flagSrc: string;
};

export type OrderCurrencyExchangeCurrencySelectorProps = {
  balances: LoadableState<OrderCurrencySourceCurrencyWithBalance>[];
  selectedCurrency?: string;
  onChange: (currencyCode: string) => void;
  hasSelectedCurrencyError: boolean;
  className?: string;
  orderRequestLoading: boolean;
  sourceComponent: OrderFormSourceComponent;
};

export const OrderCurrencyExchangeCurrencySelector = ({
  balances,
  selectedCurrency,
  onChange,
  hasSelectedCurrencyError,
  className,
  orderRequestLoading,
  sourceComponent,
}: OrderCurrencyExchangeCurrencySelectorProps) => {
  const { translation } = useLocale();
  const t = translation.orderModal.currencyExchangeForm;

  const handleCurrencyChange = useCallback(
    (balance: LoadableState<OrderCurrencySourceCurrencyWithBalance>): void => {
      if (Loadable.hasValue(balance)) {
        onChange(balance.currency);
      }
    },
    [onChange],
  );

  return (
    <div className={cssWhen(classes.container, className)}>
      <span
        data-testid="order-currency-exchange-currency-selector-label"
        className={classes.label}
      >
        {t.selectCurrency}
      </span>
      <div className={classes.row}>
        {balances.map((balance, index) => {
          const isSelected = Loadable.hasValue(balance) && balance.currency === selectedCurrency;
          const isError = isSelected && hasSelectedCurrencyError;
          const isDisabled = !Loadable.hasValue(balance) || balance.investableAmount === 0;

          return (
            <OrderCurrencyExchangeSelectionCard
              key={Loadable.hasValue(balance) ? balance.currency : `loading-${index}`}
              isSelected={isSelected}
              error={isError}
              onClick={() => handleCurrencyChange(balance)}
              className={classes.card}
              disabled={isDisabled}
              readOnly={orderRequestLoading}
            >
              <CurrencySelectorItem
                balance={balance}
                selectedCurrency={selectedCurrency}
                sourceComponent={sourceComponent}
              />
            </OrderCurrencyExchangeSelectionCard>
          );
        })}
      </div>
    </div>
  );
};

type CurrencySelectorItemProps = {
  balance: LoadableState<OrderCurrencySourceCurrencyWithBalance>;
  selectedCurrency?: string;
  sourceComponent: OrderFormSourceComponent;
};

const CurrencySelectorItem = ({ balance, selectedCurrency, sourceComponent }: CurrencySelectorItemProps) => {
  const { formatNumberRoundOrF2 } = useFormatter();

  return (
    <>
      <div className={classes.cardHeader}>
        <NbIndefiniteLoaderValueWrapper
          loaderProps={{ size: 40, height: 20 }}
          loadableValue={balance}
          loadedValueCb={value => (
            <>
              <CurrencyFlag
                currency={value.currency}
                className={classes.flag}
              />
              <span
                className={cssWhen(classes.currencyCode, [selectedCurrency === value.currency, classes.isSelectedText])}
                data-testid={`order-currency-exchange-currency-selector-${value.currency}-label`}
              >
                {value.currency}
              </span>
            </>
          )}
        />
      </div>

      <span className={cssWhen(classes.balance)}>
        <MoneyMaskHOC
          isInverse
          size={40}
          bypass={sourceComponent === OrderFormSourceComponent.Modal}
        >
          <NbIndefiniteLoaderValueWrapper
            loaderProps={{ size: 40 }}
            loadableValue={balance}
            loadedValueCb={value => (
              <span
                className={cssWhen([selectedCurrency === value.currency, classes.isSelectedText])}
                data-testid={`order-currency-exchange-currency-selector-${value.currency}-balance`}
              >
                {formatNumberRoundOrF2(value.investableAmount ?? 0)}
              </span>
            )}
          />
        </MoneyMaskHOC>
      </span>
    </>
  );
};
